import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.css';
import '../../ui/css/App.css';
import '../../ui/css/DialogFooter.css';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import AvgTransportSpeed from 'pages/AvgTransportSpeed';
import Calendars from 'pages/calendar/Calendars';
import CalendarAssignments from 'pages/calendarAssignments/CalendarAssignments';
import History from 'pages/calendarAssignments/logs/Logs';
import ComplexRole from 'pages/ComplexRole';
import DepartmentAssignmentHistory from 'pages/departmentAssignmentHistory';
import GeographicalZones from 'pages/GeographicalZones';
import MeasureUnit from 'pages/MeasureUnit';
import PersonAssignmentHistory from 'pages/personAssignmentHistory';
import { addLocale, locale } from 'primereact/api';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';

import { isPageEnabledByProductEdition } from '../../config/productEdition';
import { useCheckAuth } from '../../hooks/useCheckAuth';
import { ruLocale } from '../../lang/ruLocale';
import Absences from '../../pages/Absences';
import AssetParameters from '../../pages/AssetParameters';
import Assets from '../../pages/assets/Assets';
import AssetTypes from '../../pages/AssetTypes';
import FeedbackPage from '../../pages/FeedbackPage';
import FunctionalStructure from '../../pages/functionalStructure/FunctionalStructure';
import LicenseExpiredPage from '../../pages/LicenseExpiredPage';
import LoginPage from '../../pages/LoginPage';
import MainPage from '../../pages/MainPage';
import NotAvailablePage from '../../pages/NotAwailablePage';
import OperationTypes from '../../pages/OperationTypes';
import PlanningAlgorithms from '../../pages/PlanningAlgorithms';
import PrivacyPpolicy from '../../pages/PrivacyPpolicy';
import ProcessingWorkOrdersPage from '../../pages/ProcessingWorkOrdersPage';
import Roles from '../../pages/Roles';
import Schedules from '../../pages/Schedules';
import SetPasswordPage from '../../pages/SetPasswordPage';
import SkillsAndCertifications from '../../pages/SkillsAndCertifications';
import TaskClosingCodes from '../../pages/TaskClosingCodes';
import TaskStatuses from '../../pages/TaskStatuses';
import TasksType from '../../pages/TasksType';
import TestPage from '../../pages/Test';
import WorkOrderAutoDistributionHistory from '../../pages/WorkOrderAutoDistributionHistory';
import { setElementUIs } from '../../state/auth/authSlice';
import { RootState } from '../../state/store';
import Header from '../../ui/Header';
import SidebarNavigation from '../../ui/SidebarNavigation';
import TaskCard from '../../ui/taskCard/TaskCard';
import {
  generateStyleRules,
  transformVisibilityArrayToMap
} from '../../utils/rolesHelpters';
import VisibilityWrapper from '../../utils/wrappers/VisibilityWrapper';
import EmployeesTable from '../employeestable/EmployeesTable';

const FULL_ACCESS_ELEMENT_UIS = [
  {
    code: 'wholeui__roles',
    name: 'Весь UI',
    visible: true,
    id: 1
  }
];

const App = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isTestMode, setIsTestMode] = useState(
    localStorage.getItem('isTestMode') === 'true'
  );
  const didInitTestModeRef = useRef(false);

  const dispatch = useDispatch();
  const location = useLocation();
  const isLoaded = useSelector((state: RootState) => state.auth.isLoaded);
  const elementUIs = useSelector((state: RootState) => state.auth.elementUIs);
  const isLicenseExpired = useSelector(
    (state: RootState) => state.license.isExpired
  );
  
  // Проверяем, находимся ли мы на странице privacy_policy
  const isPrivacyPolicyPage = location.pathname === '/privacy_policy';

  addLocale('ru', ruLocale.ru);
  locale('ru');

  useCheckAuth();

  const visibilityClasses = transformVisibilityArrayToMap(elementUIs);

  // Helper to check page visibility based on elementUIs
  // Начало логики с ролевкой
  const isPageVisible = (code: string) => {
    if (!isPageEnabledByProductEdition(code)) return false;
    return (
      visibilityClasses['wholeui__roles'] === true ||
      visibilityClasses[code] === true
    );
  };

  const hiddenStyleRules = generateStyleRules(
    visibilityClasses,
    Object.keys(visibilityClasses).filter(
      (className) =>
        !className.endsWith('card__roles') && // Исключаем card__roles
        !className.endsWith('nav-btn__roles') // Исключаем все классы, оканчивающиеся на nav-btn__roles
    )
  );

  useEffect(() => {
    if (hiddenStyleRules) {
      const styleElement = document.createElement('style');
      styleElement.textContent = hiddenStyleRules;
      document.head.appendChild(styleElement);

      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [hiddenStyleRules]);
  // конец логики по ролевке

  // TODO добавлено для тестирования
  const toggleTestMode = useCallback(() => {
    const newTestMode = !isTestMode;
    setIsTestMode(newTestMode);
    localStorage.setItem('isTestMode', String(newTestMode));

    if (newTestMode) {
      // Store the original elementUIs and switch to full access mode
      if (!localStorage.getItem('originalElementUIs')) {
        localStorage.setItem('originalElementUIs', JSON.stringify(elementUIs));
      }
      dispatch(setElementUIs(FULL_ACCESS_ELEMENT_UIS));
    } else {
      // Restore the original elementUIs from local storage
      const originalElementUIs = localStorage.getItem('originalElementUIs');
      if (originalElementUIs) {
        dispatch(setElementUIs(JSON.parse(originalElementUIs)));
        localStorage.removeItem('originalElementUIs');
      }
    }
  }, [dispatch, elementUIs, isTestMode]);

  // Effect to set initial elementUIs based on test mode
  useEffect(() => {
    if (didInitTestModeRef.current) return;
    didInitTestModeRef.current = true;
    if (isTestMode) {
      toggleTestMode(); // Enable full access if test mode is active
    }
  }, [isTestMode, toggleTestMode]); // Only run once via ref guard
  // TODO ^
  // TODO | end

  if (!isLoaded) {
    return <ProgressSpinner style={{ width: '100%', marginTop: '30px' }} />;
  }

  // Если лицензия истекла — блокируем работу стенда, экран показываем всегда (в т.ч. после logout)
  if (isLicenseExpired) {
    return <LicenseExpiredPage />;
  }

  return (
    <div
      className={
        'h-full ' +
        (visibilityClasses['wholeui__roles'] ? 'whole-ui-role-active' : '')
      }
    >
      <Header setSidebarVisible={setSidebarVisible} />
      {!isPrivacyPolicyPage && (
        <SidebarNavigation
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          passedClassName={
            visibilityClasses['wholeui__roles'] ? 'whole-ui-role-active' : ''
          }
        />
      )}

      {/* Тестовый режим, кнопка для отображения скрытых элементов */}
      {/* <div style={{ textAlign: 'center', margin: '20px', position: 'absolute', right: '0', zIndex:'44'}}>
        <ToggleButton 
          checked={isTestMode} 
          onChange={toggleTestMode} 
          onLabel="Скрыть недоступные элементы" 
          offLabel="Отобразить скрытые элементы"
        />
      </div> */}
      {/* Конец кода для тестирования */}

      <Routes>
        <Route
          path="test"
          element={isPageVisible('test') ? <TestPage /> : <NotAvailablePage />}
        />
        <Route path="license-expired" element={<LicenseExpiredPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="set-password" element={<SetPasswordPage />} />
        <Route path="privacy_policy" element={<PrivacyPpolicy />} />
        <Route
          path="/"
          element={isPageVisible('tasks') ? <MainPage /> : <NotAvailablePage />}
        />
        <Route
          path="/:pageId"
          element={
            <VisibilityWrapper code="task-card__roles">
              <TaskCard />
            </VisibilityWrapper>
          }
        />

        <Route
          path="history"
          element={
            //   <VisibilityWrapper
            //   code="task-card__roles"
            // >
            // TODO переименовать согласно сервису на log
            <History />
            // </VisibilityWrapper>
          }
        />
        <Route
          path="/employeesTable"
          element={
            isPageVisible('employeesTable') ? (
              <EmployeesTable />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/absences"
          element={
            isPageVisible('absences') ? <Absences /> : <NotAvailablePage />
          }
        />
        <Route
          path="/feedback"
          element={
            isPageVisible('show-feedback-btn__roles') ? (
              <FeedbackPage />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="assets"
          element={isPageVisible('assets') ? <Assets /> : <NotAvailablePage />}
        />
        <Route
          path="calendars"
          element={
            isPageVisible('calendars') ? <Calendars /> : <NotAvailablePage />
          }
        />
        <Route
          path="/processingWorkOrders"
          element={
            isPageVisible('processingWorkOrders') ? (
              <ProcessingWorkOrdersPage />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/tasksType"
          element={
            isPageVisible('tasksType') ? <TasksType /> : <NotAvailablePage />
          }
        />
        <Route
          path="/roles"
          element={isPageVisible('roles') ? <Roles /> : <NotAvailablePage />}
        />
        <Route
          path="/geographicalZones"
          element={isPageVisible('geographicalZones') ? <GeographicalZones /> : <NotAvailablePage />}
        />
        <Route
          path="/complexRole"
          element={isPageVisible('complexRoles') ? <ComplexRole /> : <NotAvailablePage />}
        />
        <Route
          path="/assetTypes"
          element={
            isPageVisible('assetTypes') ? <AssetTypes /> : <NotAvailablePage />
          }
        />
        <Route
          path="/taskClosingCodes"
          element={
            isPageVisible('taskClosingCodes') ? (
              <TaskClosingCodes />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/operationTypes"
          element={
            isPageVisible('operationTypes') ? (
              <OperationTypes />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/planningAlgorithms"
          element={
            isPageVisible('planningAlgorithms') ? (
              <PlanningAlgorithms />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/schedules"
          element={
            isPageVisible('schedules') ? <Schedules /> : <NotAvailablePage />
          }
        />
        <Route
          path="/calendarAssignments"
          element={
            isPageVisible('calendarAssignments') ? (
              <CalendarAssignments />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/taskStatuses"
          element={
            isPageVisible('taskStatuses') ? (
              <TaskStatuses />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/assetParameters"
          element={
            isPageVisible('assetParameters') ? (
              <AssetParameters />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/functionalStructure"
          element={
            isPageVisible('functionalStructure') ? (
              <FunctionalStructure />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/skillsAndCertifications"
          element={
            isPageVisible('attainments') ? (
              <SkillsAndCertifications />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/avgTransportSpeed"
          element={
            isPageVisible('avgTransportSpeeds') ? (
              <AvgTransportSpeed />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/measureUnit"
          element={
            isPageVisible('measureUnits') ? (
              <MeasureUnit />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/departmentAssignmentHistory"
          element={
            isPageVisible('departmentAssignmentHistory') ? (
              <DepartmentAssignmentHistory />
            ) : (
              <NotAvailablePage />
            )
          }
        />
        <Route
          path="/personAssignmentHistory"
          element={
            isPageVisible('personAssignmentHistory') ? (
              <PersonAssignmentHistory />
            ) : (
              <NotAvailablePage />
            )
          }
        />
      </Routes>
    </div>
  );
};

export default App;
